import { CELL, GROUND } from '../core/config';
import { MATERIALS, key, type City, type Place, type Building, type RoadEdge } from './city';
import type { Vec } from '../destruction/structure';
import { roadLaneOffset } from './roads';
export interface BusStop {
  id: string;
  name: string;
  edge: number;
  from: number;
  t: number;
  point: Vec;
  direction: Vec;
  side: Vec;
  sign: number;
  queues: Place[];
}
export interface StreetTransitLayout {
  stops: BusStop[];
}

/** Road-side stops use the existing lane and an unobstructed pavement. Their
 * sign and waiting-space support are ordinary cells, not persistent markers. */
export function prepareStreetTransit(city: City): City {
  if (city.streetTransit || !city.regional || !city.roadGraph) return city;
  const nodes = new Map(city.roadGraph.nodes.map((n) => [n.id, n]));
  const eligible = city.roadGraph.edges.filter(
    (e) =>
      !e.bridgeId &&
      !e.curved &&
      e.kind !== 'airport service' &&
      e.width >= 7 &&
      Math.hypot(nodes.get(e.to)!.x - nodes.get(e.from)!.x, nodes.get(e.to)!.z - nodes.get(e.from)!.z) > 55,
  );
  const pins = [
    { x: -175, z: -150, name: 'Old Town' },
    { x: -65, z: -165, name: 'Civic Quarter' },
    { x: 175, z: -150, name: 'Eastbank' },
    { x: 280, z: -50, name: 'Market Avenue' },
    { x: 185, z: 175, name: 'Riverside' },
    { x: -60, z: 175, name: 'Hospital Gardens' },
    { x: -205, z: 150, name: 'Campus' },
    { x: -245, z: 0, name: 'West Residences' },
  ];
  let serial =
    Math.max(
      ...[
        ...city.buildings,
        ...(city.props ?? []),
        ...(city.infrastructure ?? []),
        ...(city.bridges ?? []),
      ].map((b) => b.id),
    ) + 1;
  const stops: BusStop[] = [];
  const point = (edge: RoadEdge, t: number, lateral: number): Vec => {
    const a = nodes.get(edge.from)!,
      b = nodes.get(edge.to)!,
      len = Math.hypot(b.x - a.x, b.z - a.z),
      dx = (b.x - a.x) / len,
      dz = (b.z - a.z) / len;
    return { x: a.x + (b.x - a.x) * t - dz * lateral, y: GROUND, z: a.z + (b.z - a.z) * t + dx * lateral };
  };
  for (const pin of pins) {
    const edge = eligible
      .filter((e) => !stops.some((s) => s.edge === e.id))
      .sort((a, b) => {
        const A = point(a, 0.5, 0),
          B = point(b, 0.5, 0);
        return Math.hypot(A.x - pin.x, A.z - pin.z) - Math.hypot(B.x - pin.x, B.z - pin.z);
      })[0];
    if (!edge) continue;
    const a = nodes.get(edge.from)!,
      b = nodes.get(edge.to)!,
      len = Math.hypot(b.x - a.x, b.z - a.z),
      direction = { x: (b.x - a.x) / len, y: 0, z: (b.z - a.z) / len },
      side = { x: -direction.z, y: 0, z: direction.x };
    const centre = point(edge, 0.48, roadLaneOffset(edge.width));
    const queuePoint = point(edge, 0.48 + 3.2 / len, edge.width / 2 + 0.8);
    const pole = point(edge, 0.48 + 7 / len, edge.width / 2 + 1.75),
      id = serial++;
    const sign: Building = {
      id,
      name: `${pin.name} bus stop`,
      kind: 'road',
      category: 'prop',
      x: pole.x - 0.09,
      z: pole.z - 0.09,
      baseY: GROUND,
      w: 1,
      d: 1,
      h: 3,
      color: 0x5f8989,
      accent: 0xe8d9b4,
      cells: [],
    };
    for (let y = 0; y < 3; y++)
      sign.cells.push({
        id: id * 1e6 + key(0, y, 0),
        x: 0,
        y,
        z: 0,
        hp: MATERIALS.metal.strength,
        material: 'metal',
        role: 'column',
        root: y === 0,
        color: y === 2 ? 0xc8d8b9 : 0x627b75,
        bounds: {
          x: 0,
          y: 0,
          z: 0,
          w: (y === 2 ? 0.62 : 0.18) / CELL,
          h: (y === 2 ? 0.62 : CELL) / CELL,
          d: 0.18 / CELL,
        },
      });
    (city.props ??= []).push(sign);
    const queues = Array.from({ length: 8 }, (_, i): Place => ({
      id: `bus-queue:${stops.length}:${i}`,
      kind: 'stop',
      name: `${pin.name} · local bus`,
      floor: 0,
      capacity: 1,
      duration: 18,
      x: queuePoint.x - direction.x * i * 0.8,
      y: GROUND + 0.12,
      z: queuePoint.z - direction.z * i * 0.8,
      supportCell: sign.cells[0].id,
    }));
    (city.places ??= []).push(...queues);
    stops.push({
      id: `bus-stop:${stops.length}`,
      name: pin.name,
      edge: edge.id,
      from: edge.from,
      t: 0.48,
      point: centre,
      direction,
      side,
      sign: id,
      queues,
    });
  }
  city.streetTransit = { stops };
  return city;
}
